import { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import * as THREE from "three";

const LIME = "#A3E635";

function TorusCore() {
  const mesh = useRef();
  const wire = useRef();
  useFrame((state, delta) => {
    const { x, y } = state.pointer;
    if (mesh.current) {
      mesh.current.rotation.x += delta * 0.18;
      mesh.current.rotation.y += delta * 0.24;
      mesh.current.rotation.x = THREE.MathUtils.lerp(mesh.current.rotation.x, y * 0.6, 0.04);
      mesh.current.rotation.y = THREE.MathUtils.lerp(mesh.current.rotation.y, x * 0.9, 0.04);
    }
    if (wire.current) {
      wire.current.rotation.x -= delta * 0.08;
      wire.current.rotation.y -= delta * 0.11;
    }
  });
  return (
    <group>
      <Float speed={1.6} rotationIntensity={0.6} floatIntensity={1.4}>
        <mesh ref={mesh}>
          <torusKnotGeometry args={[1.15, 0.34, 220, 36]} />
          <meshStandardMaterial
            color={LIME}
            emissive={LIME}
            emissiveIntensity={0.55}
            metalness={0.85}
            roughness={0.22}
            wireframe
          />
        </mesh>
      </Float>
      <mesh ref={wire} scale={1.7}>
        <icosahedronGeometry args={[1.15, 1]} />
        <meshBasicMaterial color="#3a4a1f" wireframe transparent opacity={0.35} />
      </mesh>
    </group>
  );
}

function Particles({ count = 900 }) {
  const points = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 3 + Math.random() * 7;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count]);

  useFrame((state, delta) => {
    if (points.current) {
      points.current.rotation.y += delta * 0.05;
      points.current.rotation.x = THREE.MathUtils.lerp(
        points.current.rotation.x,
        state.pointer.y * 0.25,
        0.03
      );
    }
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.035} color={LIME} transparent opacity={0.65} sizeAttenuation />
    </points>
  );
}

export const Hero3D = () => {
  return (
    <div data-testid="3d-canvas-wrapper" className="absolute inset-0 z-0 opacity-45 sm:opacity-60 lg:opacity-100">
      <Canvas
        camera={{ position: [0, 0, 5.2], fov: 52 }}
        dpr={[1, 1.75]}
        gl={{ antialias: true, alpha: true }}
      >
        <ambientLight intensity={0.4} />
        <pointLight position={[6, 6, 6]} intensity={60} color={LIME} />
        <pointLight position={[-6, -4, 2]} intensity={30} color="#ffffff" />
        <TorusCore />
        <Particles />
      </Canvas>
    </div>
  );
};
